import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Upload, Download, Archive, Eye, Pencil, RotateCcw, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic Master Data CRUD page.
 * @param props.title          Heading
 * @param props.subtitle       Subheading
 * @param props.entity         URL key (customers, insurance, body-shops, technicians, vendors)
 * @param props.testId         data-testid prefix
 * @param props.columns        [{key, label, render?}] table columns
 * @param props.fields         [{key, label, type, options?, required?, span?}] form fields
 * @param props.defaultForm    Initial form object
 * @param props.detailRoute    function(row) => path (optional, for "View" button)
 * @param props.icon           Lucide icon component
 */
export default function MasterDataPage({ title, subtitle, entity, testId, columns, fields, defaultForm, detailRoute, icon: Icon }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (showArchived) params.set("archived", "true");
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/master/${entity}${qs}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showArchived]);

  const submit = async () => {
    const required = fields.filter((f) => f.required).map((f) => f.key).filter((k) => !form[k]);
    if (required.length) return toast.error(`Required: ${required.join(", ")}`);
    try {
      if (editingId) {
        await api.patch(`/master/${entity}/${editingId}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/master/${entity}`, form);
        toast.success("Created");
      }
      setOpen(false);
      setEditingId(null);
      setForm(defaultForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const archive = async (id, name) => {
    if (!window.confirm(`Arquivar "${name || 'este registro'}"? Ele pode ser restaurado clicando em "Mostrar arquivados".`)) return;
    try {
      await api.delete(`/master/${entity}/${id}`);
      toast.success("Arquivado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao arquivar"); }
  };

  const restore = async (id) => {
    try {
      await api.patch(`/master/${entity}/${id}`, { archived: false, archived_at: null });
      toast.success("Restaurado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao restaurar"); }
  };

  const [reconciling, setReconciling] = useState(false);
  const hasReconcile = entity === "customers" || entity === "body-shops";

  const runReconcile = async () => {
    if (!window.confirm(
      "Reconciliação vai preencher registros faltantes entre Master Data e CRM. É seguro rodar várias vezes."
    )) return;
    setReconciling(true);
    try {
      const r = await api.post(`/master/${entity}/reconcile`);
      const d = r.data || {};
      toast.success(
        `Reconciliado: ${d.created_master_from_crm || 0} do CRM → Master · ${d.created_crm_from_master || 0} do Master → CRM · ${d.unified_ids || 0} ids unificados`,
        { duration: 6000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile falhou");
    } finally {
      setReconciling(false);
    }
  };

  const edit = (row) => {
    const f = { ...defaultForm };
    fields.forEach((fd) => { if (row[fd.key] !== undefined) f[fd.key] = row[fd.key]; });
    setForm(f);
    setEditingId(row.id);
    setOpen(true);
  };

  const exportCsv = () => {
    const url = `${api.defaults.baseURL}/master/export/${entity}`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${entity}.csv`;
        a.click();
        toast.success(`Exported ${entity}.csv`);
      });
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/master/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted} rows (skipped ${r.data.skipped})`);
      load();
    } catch (err) {
      toast.error("Import failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid={`${testId}-page`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center shrink-0">
              <Icon className="w-6 h-6 text-blue-400" />
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Master Data</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
            <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {hasReconcile && (
            <Button
              data-testid={`${testId}-reconcile-btn`}
              variant="outline"
              onClick={runReconcile}
              disabled={reconciling}
              className="border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10"
              title="Sincroniza Master Data ↔ CRM. Rode uma vez após deploy se estiver faltando registros em algum lugar."
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reconciling ? "animate-spin" : ""}`} />
              Reconciliar Master ↔ CRM
            </Button>
          )}
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid={`${testId}-import-input`} />
          <Button data-testid={`${testId}-import-btn`} variant="outline" onClick={() => fileRef.current?.click()} className="border-slate-700 bg-slate-900/40">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button data-testid={`${testId}-export-btn`} variant="outline" onClick={exportCsv} className="border-slate-700 bg-slate-900/40">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(defaultForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid={`${testId}-add-btn`} className="bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4 mr-2" />New</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0B1120] border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingId ? `Edit ${title}` : `New ${title.replace(/s$/, "")}`}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {fields.map((f) => (
                  <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                    <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">
                      {f.label}{f.required && <span className="text-red-400 ml-1">*</span>}
                    </Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        data-testid={`${testId}-field-${f.key}`}
                        className="mt-1.5 bg-[#030712] border-slate-800 min-h-[80px]"
                        value={form[f.key] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      />
                    ) : f.type === "select" ? (
                      <select
                        data-testid={`${testId}-field-${f.key}`}
                        className="mt-1.5 w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm"
                        value={form[f.key] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      >
                        <option value="">— Select —</option>
                        {f.options.map((o) => (
                          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                        ))}
                      </select>
                    ) : f.type === "checkbox" ? (
                      <label className="flex items-center gap-2 mt-2 text-sm text-slate-300">
                        <input
                          type="checkbox"
                          data-testid={`${testId}-field-${f.key}`}
                          checked={!!form[f.key]}
                          onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                          className="w-4 h-4"
                        />
                        {f.checkboxLabel || "Enable"}
                      </label>
                    ) : f.type === "tags" ? (
                      <Input
                        data-testid={`${testId}-field-${f.key}`}
                        className="mt-1.5 bg-[#030712] border-slate-800"
                        placeholder={f.placeholder || "comma-separated"}
                        value={Array.isArray(form[f.key]) ? form[f.key].join(", ") : (form[f.key] ?? "")}
                        onChange={(e) => setForm({
                          ...form,
                          [f.key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })}
                      />
                    ) : (
                      <Input
                        data-testid={`${testId}-field-${f.key}`}
                        type={f.type || "text"}
                        className="mt-1.5 bg-[#030712] border-slate-800"
                        value={form[f.key] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
                      />
                    )}
                  </div>
                ))}
              </div>
              <Button data-testid={`${testId}-save-btn`} onClick={submit} className="bg-blue-600 hover:bg-blue-500 w-full mt-4">
                {editingId ? "Update" : "Save"}
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid={`${testId}-search`}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-[#0B1120] border-slate-800"
          />
        </div>
        <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer transition-colors ${showArchived ? "border-amber-500/40 bg-amber-500/5 text-amber-300" : "border-slate-800 bg-[#0B1120] text-slate-400 hover:text-slate-200"}`} data-testid={`${testId}-show-archived-toggle`}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-3.5 h-3.5 accent-amber-500" />
          Mostrar arquivados
        </label>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/40 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3">{c.label}</th>
              ))}
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + 1} className="text-center text-slate-500 py-10">Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="text-center text-slate-500 py-10">
                No records. {search ? "Try clearing search." : `Click "New" to create the first one.`}
              </td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} data-testid={`${testId}-row-${row.id}`} className="border-t border-slate-800/60 hover:bg-slate-900/30 transition-colors">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-200">
                    {c.render ? c.render(row) : (row[c.key] ?? "—")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {detailRoute && (
                      <a href={detailRoute(row)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-blue-400" title="View"><Eye className="w-4 h-4" /></a>
                    )}
                    {!row.archived && (
                      <button data-testid={`${testId}-edit-${row.id}`} onClick={() => edit(row)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-amber-400" title="Edit"><Pencil className="w-4 h-4" /></button>
                    )}
                    {row.archived ? (
                      <button data-testid={`${testId}-restore-${row.id}`} onClick={() => restore(row.id)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-emerald-400" title="Restaurar"><RotateCcw className="w-4 h-4" /></button>
                    ) : (
                      <button data-testid={`${testId}-archive-${row.id}`} onClick={() => archive(row.id, row.company_name || row.name || row.title)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-transparent hover:border-red-500/30" title="Apagar / Arquivar"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500">{rows.length} record(s)</div>
    </div>
  );
}
